import { useState } from "react";
import {
  Blade,
  Button,
  Container,
  DataGrid,
  Divider,
  Toast as toast,
  TypoGraph,
} from "components";
import { TextField } from "components/controllers";
import {
  addPassportReturnNote,
  updatePassportReturnNotification,
} from "api";
import { IPassportReturnNotification } from "interfaces";
import { passportReturnDetailsLabel } from "constants/tableData";
import colors from "theme/colors";
import styles from "./passportReturns.module.scss";

const ALL_PAYMENT_TYPES = 99;

interface Props {
  row: IPassportReturnNotification;
  onClose: () => void;
  onUpdated: (updated: IPassportReturnNotification) => void;
  onOpenItem: (row: IPassportReturnNotification) => void;
}

// Stable component so Item ID / Note keystrokes don't remount inputs via the parent page
const PassportReturnBlade = (props: Props) => {
  const { row, onClose, onUpdated, onOpenItem } = props;
  const [itemIdDraft, setItemIdDraft] = useState(
    row.itemId != null ? String(row.itemId) : ""
  );
  const [noteDraft, setNoteDraft] = useState("");
  const [savingItem, setSavingItem] = useState(false);
  const [savingStatus, setSavingStatus] = useState(false);
  const [addingNote, setAddingNote] = useState(false);

  const detailColumns = passportReturnDetailsLabel.details.map((col) => ({
    ...col,
  }));

  const saveItemId = async () => {
    const trimmed = itemIdDraft.trim();
    const itemId = trimmed === "" ? 0 : Number.parseInt(trimmed, 10);
    if (trimmed !== "" && Number.isNaN(itemId)) {
      toast({ title: "Invalid item id", subTitle: "Enter a numeric item id" });
      return;
    }
    setSavingItem(true);
    try {
      const res = await updatePassportReturnNotification(row.id, { itemId });
      onUpdated(res.data);
      setItemIdDraft(res.data.itemId != null ? String(res.data.itemId) : "");
      toast({
        title: "Item linked",
        subTitle: `Return ${res.data.passportReturnId} updated`,
      });
    } finally {
      setSavingItem(false);
    }
  };

  const toggleFundsStatus = async () => {
    setSavingStatus(true);
    try {
      const res = await updatePassportReturnNotification(row.id, {
        fundsCollected: !row.fundsCollected,
      });
      onUpdated(res.data);
      toast({
        title: res.data.fundsCollected
          ? "Marked collected"
          : "Marked outstanding",
        subTitle: `Return ${res.data.passportReturnId}`,
      });
    } finally {
      setSavingStatus(false);
    }
  };

  const addNote = async () => {
    if (!noteDraft.trim()) {
      return;
    }
    setAddingNote(true);
    try {
      const res = await addPassportReturnNote(row.id, noteDraft.trim());
      onUpdated(res.data);
      setNoteDraft("");
      toast({
        title: "Note added",
        subTitle: `Return ${res.data.passportReturnId}`,
      });
    } finally {
      setAddingNote(false);
    }
  };

  return (
    <Blade
      show
      title={`Return ${row.passportReturnId}`}
      handleClose={onClose}
      headerChipProp={{
        label: row.fundsCollected ? "Collected" : "Outstanding",
        className: row.fundsCollected ? "successChip" : "errorChip",
      }}
      content={
        <Container className={styles.bladeContent}>
          <Container className={styles.dataGridContainer}>
            <DataGrid columns={detailColumns} data={row} />
          </Container>

          <Divider title="ITEM" />
          <TextField
            id="passport-return-item-id"
            label="Item ID"
            type="numericOnly"
            value={itemIdDraft}
            onChange={(e) =>
              setItemIdDraft(e.target.value.replace(/\D/g, ""))
            }
            placeholder="Link a payment item id"
          />
          {row.itemId != null && (
            <TypoGraph
              variant="body1"
              color={colors.primary}
              link
              onClick={() => onOpenItem(row)}
            >
              {`Open item ${row.itemId}`}
            </TypoGraph>
          )}
          <Container className={styles.bladeActions}>
            <Button
              id="save-passport-return-item"
              label={savingItem ? "Saving…" : "Save Item"}
              variant="outlined"
              disabled={savingItem}
              onClick={saveItemId}
            />
          </Container>

          <Divider title="NOTES" />
          {(row.notes || []).map((note, index) => (
            <Container
              key={`${note.createdAt}-${index}`}
              className={styles.noteEntry}
            >
              <TypoGraph variant="body2" content={note.text} />
              {note.createdAt && (
                <TypoGraph variant="caption" content={note.createdAt} />
              )}
            </Container>
          ))}
          <TextField
            id="passport-return-note"
            label="Note"
            value={noteDraft}
            onChange={(e) => setNoteDraft(e.target.value)}
            multiline
            rows={4}
          />
          <Container className={styles.bladeActions}>
            <Button
              id="add-passport-return-note"
              label={addingNote ? "Adding…" : "Add Note"}
              variant="outlined"
              disabled={addingNote || !noteDraft.trim()}
              onClick={addNote}
            />
          </Container>
        </Container>
      }
      footerContent={
        <Container className={styles.bladeFooter}>
          <Button
            id="toggle-passport-return-funds"
            label={
              savingStatus
                ? "Updating…"
                : row.fundsCollected
                ? "Mark as Outstanding"
                : "Mark as Collected"
            }
            variant="contained"
            color={row.fundsCollected ? "warning" : "success"}
            disabled={savingStatus}
            onClick={toggleFundsStatus}
          />
          <Button
            id="close-passport-return"
            label="Close"
            variant="outlined"
            onClick={onClose}
          />
        </Container>
      }
    />
  );
};

export default PassportReturnBlade;
export { ALL_PAYMENT_TYPES };
